import Tilt from "react-parallax-tilt";
import {About} from "./about";
import {Links} from "./links";
import {IoIosArrowBack} from "react-icons/io";
// 340px: shown at 7rem at most, so this covers 3x screens. The full-size
// profile.jpg is kept for the link preview card (scripts/og-card.html).
import photo from "../assets/profile-small.jpg";
import {Sidebar} from "./sidebar";
import React, {useEffect, useLayoutEffect, useRef, useState} from "react";
import {Link, useLocation} from "react-router-dom";
import {parentPath} from "../paths";
import {Friends} from "./friends";
import {Projects} from "./projects";
import {Photos} from "./photos";
import {NotFound} from "./notFound";
import {lite} from "../lite";
import {ScrollTimeline} from "./timeline";


// Moves are played frame by frame against the element's live position rather
// than as a CSS transition: the back button's slot scrolls with the page, and a
// fixed starting offset would let a fast scroll drag the button along with it.
// Each frame eases from where it was on screen towards wherever it is now.
// They use `translate`, not `transform`, which the sidebar's hover animation
// holds (scaleUp, forwards) and would override.
const GLIDE_MS = 450;
// ease-out with a slight overshoot, like the bouncy curve .button uses on hover
const ease = (t: number) => 1 + 2.2 * (t - 1) ** 3 + 1.2 * (t - 1) ** 2;
const glides = new WeakMap<HTMLElement, number>();

function glide(el: HTMLElement | null, from?: DOMRect) {
    if (!el || !from?.width || lite) return; // hidden (phone layout); lite mode skips animations
    cancelAnimationFrame(glides.get(el) ?? 0);
    const start = performance.now();
    const frame = (now: number) => {
        el.style.translate = "";
        const to = el.getBoundingClientRect();
        const left = 1 - ease(Math.min((now - start) / GLIDE_MS, 1));
        if (!to.width || left === 0) return void glides.delete(el);
        el.style.translate = `${(from.left - to.left) * left}px ${(from.top - to.top) * left}px`;
        glides.set(el, requestAnimationFrame(frame));
    };
    frame(start); // first frame now, before paint
}

// the sidebar shifts within the (stationary) sticky column, so animate it directly
const sidebarOf = (column: HTMLElement | null) => column?.querySelector<HTMLElement>(".side") ?? null;
const positions = (back: HTMLElement | null, card: HTMLElement | null, column: HTMLElement | null) => ({
    back: back?.getBoundingClientRect(),
    card: card?.getBoundingClientRect(),
    side: sidebarOf(column)?.getBoundingClientRect(),
});

// The name card (desktop sidebar column) and title chip (phones): back to the
// about page, with the photo tilting like the profile photo there.
const NameLink = React.forwardRef<HTMLAnchorElement, {className: string, children: React.ReactNode}>(
    function NameLink({className, children}, ref) {
        return (
            <Link ref={ref} className={className} to={"/"} aria-label={"Nour Elfangary, home"}>
                <Tilt className={"name-photo"}>
                    <img src={photo} alt=""/>
                </Tilt>
                {children}
            </Link>
        );
    });

interface BlockProps {
    active: string,
    // path after /projects/ on a project sub-page
    sub?: string,
}

export function Block({active, sub}: BlockProps) {
    const hasBack = active !== "about";
    const notFound = active === "notfound";
    const location = useLocation();

    // Desktop: once the back button scrolls up to the line where the sidebar
    // sticks, it docks above the sidebar (right-aligned with it) and the
    // sidebar shifts down to make room. Both moves are animated FLIP-style:
    // measure before, re-render in the new spot, then transition from the old
    // position to the new one. Route changes animate the same way, so the
    // sidebar glides instead of jumping when the header comes or goes (project
    // pages have none).
    const route = location.pathname;
    // the route the back button is docked on, so it undocks in the same render as a route change
    const [dockedOn, setDockedOn] = useState<string | null>(null);
    const docked = hasBack && dockedOn === route;
    const slotRef = useRef<HTMLDivElement>(null);
    const sideRef = useRef<HTMLDivElement>(null);
    const backRef = useRef<HTMLAnchorElement>(null);
    const cardRef = useRef<HTMLAnchorElement>(null);
    const before = useRef<ReturnType<typeof positions> | null>(null);
    const moved = useRef(false); // skip the back button's entrance animation once it starts moving
    const shownRoute = useRef(route);
    const measure = () => positions(backRef.current, cardRef.current, sideRef.current);

    // new route: the DOM still shows the old page here, so take the "before" positions now
    if (shownRoute.current !== route && !before.current) {
        before.current = measure();
        moved.current = !!before.current.back; // a back button that was already there doesn't re-enter
    }

    useEffect(() => {
        const slot = slotRef.current;
        const side = sideRef.current;
        const scroller = slot?.closest<HTMLElement>(".vignette");
        if (!hasBack || !slot || !side || !scroller) return;
        const stickyTop = parseFloat(getComputedStyle(side).top) || 0;
        const observer = new IntersectionObserver(([entry]) => {
            const rootTop = entry.rootBounds?.top ?? 0;
            const next = entry.intersectionRatio < 1 && entry.boundingClientRect.top < rootTop;
            setDockedOn(prev => {
                if ((prev === route) !== next) {
                    before.current = positions(backRef.current, cardRef.current, side);
                    // a fast scroll can carry the button well past the line before
                    // this fires; dock from the line, not from off the top of the screen
                    const back = before.current.back;
                    if (next && back && back.top < rootTop) {
                        before.current.back = new DOMRect(back.x, rootTop, back.width, back.height);
                    }
                    moved.current = true;
                }
                return next ? route : null;
            });
        }, {root: scroller, rootMargin: `-${stickyTop}px 0px 0px 0px`, threshold: [1]});
        observer.observe(slot);
        return () => observer.disconnect();
    }, [route, hasBack]);

    useLayoutEffect(() => {
        const routeChanged = shownRoute.current !== route;
        shownRoute.current = route;
        const prev = before.current;
        before.current = null;
        if (!prev) return;
        // App.tsx scrolls to the top on a route change, but only after this
        // runs; do it now so the end positions are the ones that get painted
        if (routeChanged) sideRef.current?.closest(".vignette")?.scrollTo({top: 0});
        glide(backRef.current, prev.back);
        glide(cardRef.current, prev.card);
        glide(sidebarOf(sideRef.current), prev.side);
    }, [docked, route]);

    const backLink = (
        <Link ref={backRef} className={`button${moved.current ? " moved" : ""}`} id={"back"} to={notFound ? "/" : parentPath(location.pathname)}>
            <IoIosArrowBack />
            {notFound ? "home" : "back"}
        </Link>
    );

    return (
        <div className="block">
            {hasBack && (
                // phones and tablets: the name card's stand-in above the title (desktop has the card in the sidebar column)
                <NameLink className={"title-chip"}>
                    <span>Nour Elfangary</span>
                </NameLink>
            )}
            {!sub && <header className={hasBack ? "compact" : undefined}>
                <div className="title">
                    {active === "about" && <h1>Nour Elfangary</h1>}
                    {active === "projects" && <h2>Projects</h2>}
                    {active === "photos" && <h2>Photos</h2>}
                    {active === "friends" && <h2>Friends</h2>}
                    {notFound && <h2><span className="nf-code">404</span>Page not found</h2>}
                </div>
                {active === "about" && (
                    <div className={"panel"}>
                        <Tilt className={"profile-icon"}>
                            <img className={"icon"} src={photo} alt="Nour Elfangary"/>
                        </Tilt>
                        <Links className={"miniLinks"}/>
                        <Tilt className={"profile"}>
                            <img className={"photo"} src={photo} alt="Nour Elfangary"/>
                        </Tilt>
                    </div>
                )}
            </header>}
            <main>
                <div className={`side-column${hasBack && !sub ? " beside-title" : ""}`} ref={sideRef}>
                    {hasBack && docked && backLink}
                    {hasBack && (
                        <NameLink ref={cardRef} className={"name-card"}>
                            <span>Nour<br/>Elfangary</span>
                        </NameLink>
                    )}
                    <Sidebar id={"mainScreen"}/>
                </div>
                <div className={"content"}>
                    {hasBack && (
                        <div className={"back-slot"} ref={slotRef}>
                            {!docked && backLink}
                        </div>
                    )}
                    <About className={active === "about" ? "show" : "hide"}/>
                    <Projects className={active === "projects" ? "show" : "hide"} sub={sub}/>
                    <Photos className={active === "photos" ? "show" : "hide"}/>
                    <Friends className={active === "friends" ? "show" : "hide"}/>
                    {notFound && <NotFound className="show"/>}
                </div>
            </main>
            {!lite && <ScrollTimeline trips={active === "photos" && !sub} route={route}/>}
        </div>
    );
}
